// ✅ src/pages/VehicleV2/TypePositionListing.jsx  (FULL FILE)
// Backend mount:
//   app.use("/api/typePositionListing", typePositionListing);
//
// Endpoints expected:
//   GET  https://nissi-india.com/api/typePositionListing/list?search=&page=1&limit=10
//   POST https://nissi-india.com/api/typePositionListing/delete   body: { ids:[1,2] }
//
// ✅ Notes:
// - Uses your common localStorage header pattern
// - Supports pagination, search, multi-delete
// - Edit opens: /vehicle/VehicleTypePosition?typePositionId=..
// - Add opens: /vehicle/VehicleTypePosition

import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaPlus, FaSearch, FaFilter, FaEdit, FaTrash, FaTimes } from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/typePositionListing";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toStr = (v, def = "") => {
  if (v === undefined || v === null) return def;
  return String(v).trim();
};

const VehicleTypePositionListing = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ Your shared localStorage pattern
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  // Rights / BranchActive
  const canAdd = useMemo(() => {
    const v = sessionStorage.getItem("typepos_addrights");
    if (v === null) return true;
    return v === "1" || v === "true";
  }, []);

  const canDelete = useMemo(() => {
    const v = sessionStorage.getItem("typepos_deleterights");
    if (v === null) return true;
    return v === "1" || v === "true";
  }, []);

  const branchActive = useMemo(() => {
    const v = sessionStorage.getItem("BranchActive");
    if (v === null) return true;
    return v === "1" || v === "true";
  }, []);

  // Filters
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(true);

  // Table data
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // Selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);

  const totalPages = useMemo(() => {
    return limit > 0 ? Math.max(1, Math.ceil(total / limit)) : 1;
  }, [total, limit]);

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  /* ------------------------- LOAD LIST ------------------------- */
  const fetchList = useCallback(
    async (opts = {}) => {
      const nextPage = opts.page ?? page;
      const nextLimit = opts.limit ?? limit;
      const nextSearch = opts.search ?? search;

      try {
        setLoading(true);

        const { data } = await axios.get(`${API_BASE}/list`, {
          params: {
            search: nextSearch,
            page: nextPage,
            limit: nextLimit,
          },
          headers: commonHeaders,
        });

        if (!data?.success) {
          throw new Error(data?.message || "Failed to load type position list");
        }

        const nextRows = Array.isArray(data.rows) ? data.rows : [];
        const nextTotal = toInt(data.total, 0);

        setRows(nextRows);
        setTotal(nextTotal);

        setSelectedIds([]);
        setSelectAll(false);

        const computedTotalPages =
          nextLimit > 0 ? Math.max(1, Math.ceil(nextTotal / nextLimit)) : 1;

        if (nextPage > computedTotalPages) {
          setPage(computedTotalPages);
          return fetchList({
            page: computedTotalPages,
            limit: nextLimit,
            search: nextSearch,
          });
        }

        setPage(nextPage);
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || "Server error");
        setRows([]);
        setTotal(0);
        setSelectedIds([]);
        setSelectAll(false);
      } finally {
        setLoading(false);
      }
    },
    [page, limit, search, commonHeaders]
  );

  /* ------------------------- INIT FLOW ------------------------- */
  useEffect(() => {
    fetchList({ page: 1 });
  }, [fetchList]);

  /* ------------------------- SELECTION ------------------------- */
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
      setSelectAll(false);
      return;
    }

    const ids = rows.map((x) => toInt(x.fld_vehicletyre_positionid)).filter((x) => x > 0);
    setSelectedIds(ids);
    setSelectAll(ids.length > 0);
  };

  const toggleSelect = (id) => {
    const nid = toInt(id);
    setSelectedIds((prev) =>
      prev.includes(nid) ? prev.filter((x) => x !== nid) : [...prev, nid]
    );
  };

  useEffect(() => {
    if (!rows.length) {
      setSelectAll(false);
      return;
    }

    const ids = rows.map((x) => toInt(x.fld_vehicletyre_positionid)).filter((x) => x > 0);
    const allSelected = ids.length > 0 && ids.every((id) => selectedIds.includes(id));
    setSelectAll(allSelected);
  }, [rows, selectedIds]);

  /* ------------------------- ACTIONS ------------------------- */
  const handleSearchRecords = async () => {
    await fetchList({ page: 1, search });
  };

  const handleClearFilters = async () => {
    setSearch("");
    await fetchList({ page: 1, search: "" });
  };

  const handleAddNew = () => {
    navigate(`/vehicle/VehicleTypePosition`);
  };

  const handleEdit = (row) => {
    const id = toInt(row?.fld_vehicletyre_positionid);
    navigate(`/vehicle/VehicleTypePosition?typePositionId=${id}`);
  };

  const handleDelete = useCallback(async () => {
    if (!branchActive || !canDelete) return;

    if (selectedIds.length === 0) {
      toast.warn("Please select at least one type position to delete");
      return;
    }

    const ok = window.confirm(`Delete ${selectedIds.length} selected record(s)?`);
    if (!ok) return;

    try {
      setLoading(true);

      const { data } = await axios.post(
        `${API_BASE}/delete`,
        { ids: selectedIds },
        { headers: commonHeaders }
      );

      if (!data?.success) {
        throw new Error(data?.message || "Delete failed");
      }

      toast.success(data?.message || "Deleted successfully");

      const remaining = Math.max(0, total - selectedIds.length);
      const remainingPages = limit > 0 ? Math.max(1, Math.ceil(remaining / limit)) : 1;
      const nextPage = Math.min(page, remainingPages);

      await fetchList({ page: nextPage });
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Delete failed");
    } finally {
      setLoading(false);
    }
  }, [branchActive, canDelete, selectedIds, commonHeaders, total, limit, page, fetchList]);

  const goToPage = async (p) => {
    const np = Math.min(totalPages, Math.max(1, toInt(p, 1)));
    await fetchList({ page: np });
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VEHICLE TYPE POSITION</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black`
          }`}
        >
          {/* Header */}
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-8">
            <div className="flex items-center gap-4">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Vehicle Type Position Listing
              </h1>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {total} Records
              </span>

              {loading && <span className="text-sm opacity-70">Loading...</span>}
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setShowFilters((prev) => !prev)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800" + (showFilters ? " bg-gray-800" : "")
                    : "border-gray-300 text-gray-700 hover:bg-gray-50" + (showFilters ? " bg-gray-50" : "")
                }`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                type="button"
                onClick={handleAddNew}
                disabled={!branchActive || !canAdd}
                className={`flex items-center gap-2 text-white px-4 py-2 rounded transition-colors ${
                  !branchActive || !canAdd
                    ? "bg-gray-400 cursor-not-allowed"
                    : `${buttonClass} ${hoverClass}`
                }`}
              >
                <FaPlus className="w-4 h-4" />
                Add New
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div
              className={`rounded-xl p-6 mb-6 border ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
              }`}
            >
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <FaFilter />
                FILTRATION AREA
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className={labelClass}>Search Type Position</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaSearch className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(toStr(e.target.value))}
                      placeholder="Type to search type position..."
                      className={`pl-10 ${inputClass}`}
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium transition-colors duration-200"
                >
                  <FaTimes className="w-4 h-4" />
                  Clear Filters
                </button>

                <button
                  type="button"
                  onClick={handleSearchRecords}
                  className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200`}
                >
                  <FaSearch />
                  Search Records
                </button>
              </div>
            </div>
          )}

          {/* Table */}
          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {loading ? (
              <div className="text-center py-14 text-gray-500 dark:text-gray-300">Loading...</div>
            ) : rows.length === 0 ? (
              <div className="text-center py-16">
                <div
                  className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                    isDark ? "bg-gray-700" : "bg-gray-100"
                  }`}
                >
                  <FaSearch className="w-10 h-10 text-gray-400" />
                </div>

                <h3 className={`text-xl font-semibold mb-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                  No Type Positions Found
                </h3>

                <p className={`mb-6 max-w-sm mx-auto ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  Try adjusting your search criteria or add a new type position.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-4 text-center font-semibold">S.No</th>
                      <th className="px-4 py-4 text-center font-semibold">Type Position</th>
                      <th className="px-4 py-4 text-center font-semibold">Edit</th>
                      <th className="px-4 py-4 text-center font-semibold">
                        <div className="flex items-center justify-center">
                          Select All
                          <input
                            type="checkbox"
                            checked={selectAll}
                            onChange={handleSelectAll}
                            className="ml-2"
                          />
                        </div>
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {rows.map((r, idx) => {
                      const id = toInt(r.fld_vehicletyre_positionid);

                      return (
                        <tr
                          key={id || idx}
                          className={`hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200 ${
                            isDark ? "bg-gray-900" : "bg-white"
                          }`}
                        >
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white text-center">
                            {r.slno ?? (page - 1) * limit + idx + 1}
                          </td>

                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-center">
                            {r.fld_vehicletyre_positiontype || "-"}
                          </td>

                          <td className="px-4 py-3 text-center">
                            <button
                              type="button"
                              onClick={() => handleEdit(r)}
                              className="inline-flex items-center gap-1 px-3 py-2 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transform hover:-translate-y-0.5 transition-all duration-200 shadow-md hover:shadow-lg"
                            >
                              <FaEdit />
                              Edit
                            </button>
                          </td>

                          <td className="px-4 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(id)}
                              onChange={() => toggleSelect(id)}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mt-6">
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1}
                className={`px-3 py-2 rounded border ${
                  page <= 1
                    ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                    : isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                Prev
              </button>

              <div className={`text-sm ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                Page <b>{page}</b> / {totalPages}
              </div>

              <button
                type="button"
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages}
                className={`px-3 py-2 rounded border ${
                  page >= totalPages
                    ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                    : isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                Next
              </button>

              <select
                value={limit}
                onChange={async (e) => {
                  const nl = toInt(e.target.value, 25);
                  setLimit(nl);
                  await fetchList({ page: 1, limit: nl });
                }}
                className={`ml-2 px-2 py-2 rounded border ${
                  isDark ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"
                }`}
              >
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}/page
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleDelete}
                disabled={!branchActive || !canDelete || selectedIds.length === 0}
                className={`flex items-center gap-2 px-4 py-2 text-white text-sm rounded-lg shadow transition ${
                  !branchActive || !canDelete || selectedIds.length === 0
                    ? "bg-gray-400 cursor-not-allowed"
                    : `${buttonClass} ${hoverClass}`
                }`}
              >
                <FaTrash />
                Delete Selected
              </button>
            </div>
          </div>

          <div className={`mt-8 pt-6 border-t ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <p className={`text-sm text-center ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              INFRA SCREEN : VEHICLE TYPE POSITION LISTING
            </p>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleTypePositionListing;